import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import { SectionDescription } from "components/misc/Typography.js";
import { Container, ContentWithVerticalPadding } from "components/misc/Layouts.js";
import { Eyebrow, HighlightedText, ArrowLink, PrimaryButtonLink } from "blog/components/ui.js";

import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";
import { ReactComponent as CheckIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as DotPatternBase } from "images/dot-pattern.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-1.svg";
import PostCarousel from "blog/components/PostCarousel.js";

/**
 * Section n°1 de la page d’accueil : le hero « mission ».
 *
 * Pose la ligne éditoriale du site en une phrase (le repère de l’IA, de la tech et de
 * l’innovation dans le bassin de Besançon), trois piliers à puces et deux actions seulement :
 * lire le blog (principale) et découvrir le projet (secondaire).
 *
 * Squelette repris de components/hero/TwoColumnWithFeaturesAndTestimonial_jimmin.js
 * (sans le témoignage), primitives de blog/components/ui.js.
 * Aucune prop n’est obligatoire : les valeurs par défaut sont le texte validé.
 * C’est la seule section de la page qui porte un <h1>.
 */

/* Même largeur (max-w-screen-xl) que les autres sections ; le retrait horizontal
   est déjà fourni par le p-8 du wrapper AnimationRevealPage. */
const Row = tw.div`flex flex-col lg:flex-row justify-between items-center lg:pt-16 max-w-screen-xl mx-auto`;

/* Colonnes à part fixe (7/12 texte, 5/12 image) pour que la photo garde son ratio paysage. */
const TextColumn = tw.div`mx-auto lg:mx-0 max-w-lg lg:max-w-none lg:w-7/12 text-center lg:text-left`;
const Subheading = tw(Eyebrow)`block`;
/* Même échelle et même couleur que le hero de la DA (text-primary-900, jusqu’à 6xl). */
const Heading = tw.h1`mt-4 text-4xl sm:text-5xl xl:text-6xl font-black tracking-wide text-primary-900 leading-tight`;
const Description = tw(SectionDescription)`mt-6 text-gray-700 max-w-lg mx-auto lg:mx-0`;

/* Trois piliers à puces, comme la FeatureList du hero de la DA. */
const FeatureList = tw.ul`mt-6 leading-loose inline-block text-left list-none`;
const Feature = tw.li`flex items-center`;
const FeatureIcon = tw(CheckIcon)`w-5 h-5 text-primary-500 flex-shrink-0`;
const FeatureText = tw.span`ml-2 font-medium text-gray-700`;

const Actions = tw.div`mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start`;
/* Bouton principal du site (rounded hérité), même gabarit que « Voir tous les articles ». */
const PrimaryAction = styled(PrimaryButtonLink)`
  ${tw`px-10 py-4 w-full sm:w-auto`}
`;
/* Anneau de focus ajouté localement : ArrowLink (ui.js, lecture seule) n’a qu’un soulignement. */
const SecondaryAction = styled(ArrowLink)`
  ${tw`mt-4 sm:mt-0 sm:ml-8 rounded focus:shadow-outline`}
`;

/* Colonne droite : carrousel des derniers articles, avec le fond décalé et le motif de points de la DA. */
const ImageColumn = tw.div`mx-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-12 xl:ml-16 w-full max-w-lg lg:max-w-none lg:w-5/12 flex-shrink-0`;
const ImageOffset = tw.div`absolute inset-0 rounded-4xl bg-primary-500 opacity-10 transform translate-x-6 translate-y-6`;
const DotPattern = tw(
  DotPatternBase
)`pointer-events-none absolute right-0 bottom-0 w-32 h-32 transform translate-x-6 translate-y-6 sm:translate-x-10 sm:translate-y-10 opacity-25 text-primary-500 fill-current z-10`;

/* Décor de section : un seul blob violet en bas à gauche (blob-1, fill primary codé en dur).
   Pas de blob rose en haut à droite : « Derniers articles », juste en dessous, en porte déjà un
   sur ce bord, et le hero de la DA ne porte que le motif de points de l’image. */
const DecoratorBlob1 = tw(
  SvgDecoratorBlob1
)`pointer-events-none absolute left-0 bottom-0 h-64 w-64 opacity-15 transform -translate-x-1/2 translate-y-16 -z-10`;

const defaultFeatures = ["Actualités et analyses locales", "Cartographie de l’écosystème", "Formations et débouchés"];

export default function HeroMission({
  subheading = "IA, tech et innovation · bassin de Besançon",
  heading = (
    <>
      Le repère de l’IA, de la tech et de l’innovation à <HighlightedText>Besançon.</HighlightedText>
    </>
  ),
  description = "IA à Besançon suit l’intelligence artificielle, la tech et l’innovation dans le bassin bisontin : laboratoires, entreprises, formations, événements. Un site pour comprendre l’écosystème local et y trouver sa place.",
  features = defaultFeatures,
  primaryButtonText = "Lire le blog",
  primaryButtonUrl = "/blog",
  secondaryLinkText = "Découvrir le projet",
  secondaryLinkUrl = "/about",
  /* Nombre d’articles qui défilent dans le carrousel. */
  carouselCount = 5,
}) {
  return (
    <Container id="accueil">
      <ContentWithVerticalPadding>
        <Row>
          <TextColumn>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
            {features && features.length > 0 && (
              <FeatureList role="list">
                {features.map(feature => (
                  <Feature key={feature}>
                    <FeatureIcon aria-hidden="true" focusable="false" />
                    <FeatureText>{feature}</FeatureText>
                  </Feature>
                ))}
              </FeatureList>
            )}
            <Actions>
              <PrimaryAction to={primaryButtonUrl}>{primaryButtonText}</PrimaryAction>
              {secondaryLinkText && (
                <SecondaryAction to={secondaryLinkUrl}>
                  {secondaryLinkText}
                  <ArrowRightIcon aria-hidden="true" focusable="false" />
                </SecondaryAction>
              )}
            </Actions>
          </TextColumn>

          <ImageColumn>
            <PostCarousel
              count={carouselCount}
              decorBehind={<ImageOffset aria-hidden="true" />}
              decorFront={<DotPattern aria-hidden="true" focusable="false" />}
            />
          </ImageColumn>
        </Row>
      </ContentWithVerticalPadding>
      <DecoratorBlob1 aria-hidden="true" focusable="false" />
    </Container>
  );
}
